import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { sendHtmlEmail } from '../emailapi';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    acceptsTerms: false,
    acceptsUpdates: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const emailData = {
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
        acceptsTerms: formData.acceptsTerms ? 'Yes' : 'No',
        acceptsUpdates: formData.acceptsUpdates ? 'Yes' : 'No',
      };

      // Send to cashprodigital@gmail.com
      await sendHtmlEmail('cashprodigital@gmail.com', 'New Enquiry from Contact Form', emailData);

      setSubmitStatus('success');
      alert('Thank you! We will get back to you soon.');

      setFormData({
        fullName: '',
        email: '',
        phone: '',
        service: '',
        message: '',
        acceptsTerms: false,
        acceptsUpdates: false,
      });
    } catch (error) {
      console.error('Email sending failed:', error);
      setSubmitStatus('error');
      alert('Something went wrong. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 bg-gradient-to-br from-green-200 via-white to-green-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Info (unchanged) */}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Let’s Scale Your<br />Financial Future.
              </h2>
              <p className="text-gray-600 text-lg">
                Have questions about loans? Our experts usually respond within 2 hours.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-green-500 rounded-full"></span>
                Reach Out Directly.
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3 group">
                  <div className="p-2 bg-green-100 rounded-full group-hover:bg-green-200 transition-colors">
                    <Phone size={18} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Call Anytime</p>
                    <a href="tel:+918887141052" className="text-gray-600 hover:text-green-600 transition-colors">
                      +91 88871 41052
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3 group">
                  <div className="p-2 bg-yellow-100 rounded-full group-hover:bg-yellow-200 transition-colors">
                    <Mail size={18} className="text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Official Email</p>
                    <a href="mailto:info@cashpro.co.in" className="text-gray-600 hover:text-yellow-600 transition-colors">
                      info@cashpro.co.in
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3 group">
                  <div className="p-2 bg-green-100 rounded-full group-hover:bg-green-200 transition-colors">
                    <MapPin size={18} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Main Office</p>
                    <p className="text-gray-600">2nd Floor Bhawani Complex Sigra Rathyatra road Above SBI Bank Varanasi, Uttar Pradesh.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 group">
                  <div className="p-2 bg-yellow-100 rounded-full group-hover:bg-yellow-200 transition-colors">
                    <Clock size={18} className="text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Working Hours</p>
                    <p className="text-gray-600">Mon - Sat: 10:00 AM - 07:00 PM</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  required
                  value={formData.fullName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address (Optional)
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
                  placeholder="+91 98765 43210"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Required Service <span className="text-red-500">*</span>
                </label>
                <select
                  name="service"
                  required
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
                >
                  <option value="">Select a service</option>
                  <option value="Personal Loan">Personal Loan</option>
                  <option value="Business Loan">Business Loan</option>
                  <option value="Corporate Funding">Corporate Funding</option>
                  <option value="Home Loan">Home Loan</option>
                  <option value="Commercial Property Purchase">Commercial Property Purchase</option>
                  <option value="Industrial Property Purchase">Industrial Property Purchase</option>
                  <option value="Machinery Loan">Machinery Loan</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tell us about your requirements...
                </label>
                <textarea
                  name="message"
                  rows="3"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
                  placeholder="I need a loan for..."
                ></textarea>
              </div>
              <div className="space-y-3">
                <label className="flex items-start gap-4 rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-4 text-gray-800 shadow-sm transition hover:border-green-200 hover:bg-green-50/60 cursor-pointer">
                  <input
                    type="checkbox"
                    name="acceptsTerms"
                    required
                    checked={formData.acceptsTerms}
                    onChange={handleChange}
                    className="mt-1 h-5 w-5 shrink-0 rounded border-gray-400 text-green-600 focus:ring-2 focus:ring-green-500"
                  />
                  <span className="text-sm sm:text-base leading-relaxed">
                    I agree to all the{' '}
                    <Link to="/terms-and-conditions" className="font-semibold text-green-500 underline underline-offset-2 hover:text-orange-600">
                      Terms and Conditions
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy-policy" className="font-semibold text-green-500 underline underline-offset-2 hover:text-orange-600">
                      Privacy Policy
                    </Link>
                    .
                  </span>
                </label>
                <label className="flex items-start gap-4 rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-4 text-gray-800 shadow-sm transition hover:border-green-200 hover:bg-green-50/60 cursor-pointer">
                  <input
                    type="checkbox"
                    name="acceptsUpdates"
                    checked={formData.acceptsUpdates}
                    onChange={handleChange}
                    className="mt-1 h-5 w-5 shrink-0 rounded border-gray-400 text-green-600 focus:ring-2 focus:ring-green-500"
                  />
                  <span className="text-sm sm:text-base leading-relaxed">
                    I want to receive CashPro updates, promotions, and blog updates via Email, SMS, WhatsApp, and RCS messages.
                  </span>
                </label>
              </div>
              <button
                type="submit"
                disabled={isSubmitting || !formData.acceptsTerms}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Submit Inquiry'}
                {!isSubmitting && <Send size={16} className="group-hover:translate-x-1 transition-transform" />}
              </button>
              {submitStatus === 'success' && (
                <p className="text-green-600 text-sm text-center mt-2">Message sent successfully!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-600 text-sm text-center mt-2">Failed to send. Please try again.</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
